import { removePrefixes } from '~/components/CandidateList/utils';
import { useCheckPaymentExpiration } from '~/domain/Payments';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IJob, IUser, ProfessionTypeEnum } from '~/models';
import { filterObjectKeys } from '~/utils/Object';
import { toExperienceLabel } from '~/utils/professional';

/**
 * The specialty keys a candidate holds for the shift's profession, e.g.
 * `['specialty_General Dentistry']`.
 *
 * Exported as a pure function — not just returned by the hook — because a
 * candidate *list* needs the same signal per row and the rules of hooks forbid
 * calling `useCandidate` in a loop. Keeping one implementation is the point: the
 * hook below builds `userSpecialties` from this, so the row chip and the profile
 * panel can never disagree about what a candidate is qualified for.
 */
export const getCandidateSpecialties = (
  candidate: IUser | null | undefined,
  job: Partial<IJob> | null | undefined,
): string[] => {
  const profession = (job?.profession || candidate?.license?.profession) as ProfessionTypeEnum;
  const educationInfo = candidate?.professional_info?.education?.[profession];

  return Object.keys(filterObjectKeys(educationInfo?.specialtiesAndProcedures, 'specialty'));
};

/**
 * Whether the shift names a specialty at all — i.e. whether there is a
 * requirement a candidate could be missing.
 *
 * A general-dentistry shift **does** state one: `DefaultGeneralSpecialty` is
 * literally `'specialty_General Dentistry'`, so it matches and goes through the
 * real comparison. This is `false` only for a shift that names no specialty and
 * carries no specialty skill.
 */
export const isSpecialtyShift = (job: Partial<IJob> | null | undefined): boolean =>
  Boolean(
    job?.skills?.[0]?.code?.includes('specialty') ||
    job?.specialty?.toLowerCase()?.includes('specialty'),
  );

/**
 * Whether the candidate holds the shift's preferred specialty.
 *
 * Returns `false` on a shift that names no specialty, because there is nothing
 * to match against — so this is NOT the predicate to negate when deciding
 * whether to warn about a missing specialty. Use {@link hasSpecialtyMismatch}
 * for that.
 *
 * Pure twin of the hook's `specialtyMatch`, for the same reason as
 * {@link getCandidateSpecialties}.
 */
export const hasJobSpecialtyMatch = (
  candidate: IUser | null | undefined,
  job: Partial<IJob> | null | undefined,
): boolean =>
  isSpecialtyShift(job) && getCandidateSpecialties(candidate, job).includes(job?.specialty || '');

/**
 * Whether the candidate is missing a specialty the shift actually asked for.
 *
 * Deliberately **not** `!hasJobSpecialtyMatch(...)`: that negation is also true
 * for a shift which names no specialty, and an office cannot be missing a
 * requirement it never set. The legacy warning banner used the plain negation
 * and so warned about every applicant on such a shift; the redesign's chip and
 * its SPECIALTIES alert read this instead.
 */
export const hasSpecialtyMismatch = (
  candidate: IUser | null | undefined,
  job: Partial<IJob> | null | undefined,
): boolean => isSpecialtyShift(job) && !hasJobSpecialtyMatch(candidate, job);

export const useCandidate = (user: IUser, candidate: IUser | null, job: IJob | null) => {
  const candidateLicense = candidate?.license;

  const { payments } = useCheckPaymentExpiration();
  const hasValidPayment = user?.is_pre_approved || payments?.length;

  const professionalInfo = candidate?.professional_info;
  const officeInsurances = user?.office_info?.insurances || [];
  const candidateEducation = professionalInfo?.education;
  const candidateProfession = candidateLicense?.profession;
  const profession = (job?.profession || candidateProfession) as ProfessionTypeEnum;
  const educationInfo = candidateEducation?.[profession];
  const candidateInsurances = professionalInfo?.insurances || [];
  const userSpecialties = getCandidateSpecialties(candidate, job);

  const userProcedures = Object.keys(
    filterObjectKeys(educationInfo?.specialtiesAndProcedures, 'procedure'),
  );

  const specialtiesAndProcedures = educationInfo?.specialtiesAndProcedures ?? {};
  const professionProcedures = removePrefixes(educationInfo?.specialtiesAndProcedures ?? {});

  const specialtyMatch = hasJobSpecialtyMatch(candidate, job);

  const insuranceMismatch =
    job?.profession === UserProfession.DN &&
    officeInsurances?.length > 0 &&
    candidateInsurances?.length > 0 &&
    !officeInsurances?.some((insurance) => candidateInsurances?.includes(insurance));
  const professionName = (() => {
    const subProfession = candidateLicense?.subProfession;

    if (subProfession === UserSubProfession.EFDA) {
      return 'Expanded Functions DA';
    }

    return candidateLicense?.professionName;
  })();

  const softwares = candidateEducation?.[profession]?.softwares;

  const licenseNumber = candidateLicense?.credentials?.find(
    ({ entry_field }) => entry_field?.license,
  )?.entry_field?.license;
  const issueDate = candidateLicense?.credentials?.find(
    ({ entry_field }) => entry_field?.issue_date,
  )?.entry_field?.issue_date;

  const nationalProviderIdentifier = professionalInfo?.nationalProviderIdentifier;
  const shouldShowNpi = candidateLicense?.profession === UserProfession.DN;
  const licenseState = candidateLicense?.state;

  const yearsOfExperience = toExperienceLabel(candidate);

  const candidates = useAppSelector((state) => state?.job?.candidates) || [];
  const currentCounter =
    /** @ts-expect-error candidates didn't described well in job reducer */
    candidates?.find?.((item) => item?.candidate?.id === candidate?.id) ||
    job?.multipleCounters?.find?.((it) => it?.candidate?.id === candidate?.id);
  const { candidate: candidateOffer } = currentCounter || {};

  return {
    userSpecialties,
    userProcedures,
    candidateInsurances,
    // professionProcedures is only processed specialtiesAndProcedures, use only for showing
    professionProcedures,
    specialtiesAndProcedures,
    specialtyMatch,
    insuranceMismatch,
    softwares,
    licenseNumber: hasValidPayment ? licenseNumber : '******',
    licenseState,
    issueDate,
    nationalProviderIdentifier,
    shouldShowNpi,
    professionName,
    yearsOfExperience,
    candidateOffer,
  };
};
